/**
 * 美洽网页客服（https://www.meiqia.com/help/article/javascript/）
 * - 官方插件支持文字、图片等
 * - 每次整页加载使用新 clientId + 清本地缓存，刷新后不带回旧消息
 */

const SCRIPT_ID = 'meiqia-js-sdk';
const PIN_STYLE_ID = 'meiqia-pin-corner-style';
const STORAGE_KEY_RE = /meiqia|MEIQIA|MEG[_-]|mq[_-]/i;
const BUTTON_IFRAME_SEL =
  '#meiqia-container iframe#button, #meiqia-container iframe[name="button"]';

let loadPromise = null;
let ready = false;
let initPromise = null;
let pinObserver = null;

function freshClientId() {
  // 美洽要求 6–32 位唯一字符串
  const raw =
    'g' +
    Date.now().toString(36) +
    Math.random().toString(36).slice(2, 12);
  return raw.slice(0, 32);
}

export function clearMeiqiaLocalData() {
  try {
    [window.localStorage, window.sessionStorage].forEach((storage) => {
      if (!storage) return;
      const keys = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && STORAGE_KEY_RE.test(key)) keys.push(key);
      }
      keys.forEach((key) => storage.removeItem(key));
    });
  } catch (e) {
    console.warn('[meiqia] clear storage failed', e);
  }
}

/**
 * 安装官方 stub（仅首次）。
 * 注意：脚本加载后切勿再覆盖 window._MEIQIA，否则会毁掉真实 SDK。
 */
function installMeiqiaStub() {
  const w = window;
  if (typeof w._MEIQIA === 'function') {
    return w._MEIQIA;
  }
  const q = function () {
    (q.a = q.a || []).push(arguments);
  };
  q.a = [];
  w._MEIQIA = q;
  return q;
}

function mq() {
  return window._MEIQIA;
}

/** 美洽默认 bottom/horizontal≈30px，强制贴住右下角 */
function ensurePinCornerStyle() {
  if (document.getElementById(PIN_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = PIN_STYLE_ID;
  style.textContent = `
#meiqia-container iframe#button,
#meiqia-container iframe[name="button"] {
  right: 0 !important;
  bottom: 0 !important;
  left: auto !important;
  top: auto !important;
  margin: 0 !important;
}
`;
  document.head.appendChild(style);
}

function applyButtonCornerPin() {
  ensurePinCornerStyle();
  const btn = document.querySelector(BUTTON_IFRAME_SEL);
  if (!btn) return false;
  btn.style.setProperty('right', '0', 'important');
  btn.style.setProperty('bottom', '0', 'important');
  btn.style.setProperty('left', 'auto', 'important');
  btn.style.setProperty('top', 'auto', 'important');
  btn.style.setProperty('margin', '0', 'important');
  return true;
}

function startPinCornerWatch() {
  ensurePinCornerStyle();
  applyButtonCornerPin();

  // 美洽会异步写入 bottom/horizontal（默认约 30px），短时内反复贴边
  let tries = 0;
  const timer = setInterval(() => {
    applyButtonCornerPin();
    tries += 1;
    if (tries >= 20) clearInterval(timer);
  }, 300);

  if (pinObserver || typeof MutationObserver === 'undefined') return;
  const root = document.body;
  if (!root) return;

  let scheduled = false;
  pinObserver = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      applyButtonCornerPin();
    });
  });
  pinObserver.observe(root, { childList: true, subtree: true });
}

function loadMeiqiaScript() {
  if (document.getElementById(SCRIPT_ID)) {
    return Promise.resolve();
  }
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    installMeiqiaStub();
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.charset = 'UTF-8';
    script.src = 'https://static.meiqia.com/dist/meiqia.js';
    script.onload = () => resolve();
    script.onerror = () => {
      loadPromise = null;
      reject(new Error('failed to load meiqia.js'));
    };
    const first = document.getElementsByTagName('script')[0];
    if (first && first.parentNode) {
      first.parentNode.insertBefore(script, first);
    } else {
      document.head.appendChild(script);
    }
  });

  return loadPromise;
}

/**
 * 在首页初始化美洽。
 * 官方要求：entId / manualInit 必须在 meiqia.js 执行前进入队列。
 */
export async function initMeiqia(options = {}) {
  const entId =
    options.entId ||
    process.env.VUE_APP_MEIQIA_ENT_ID ||
    '';

  if (!entId) {
    console.warn(
      '[meiqia] 未配置 VUE_APP_MEIQIA_ENT_ID，请到美洽工作台「设置 → 接入 → 网页插件」复制企业 ID'
    );
    return false;
  }

  if (ready) {
    return true;
  }
  if (initPromise) {
    return initPromise;
  }

  const clearHistory = options.clearHistory !== false;
  if (clearHistory) {
    clearMeiqiaLocalData();
  }

  initPromise = (async () => {
    const clientId = freshClientId();

    // 1) 先装 stub，并立刻排队配置（必须在脚本执行前）
    const stub = installMeiqiaStub();
    stub('entId', String(entId));
    stub('manualInit');
    stub('clientId', clientId);

    // 2) 再加载官方脚本
    try {
      await loadMeiqiaScript();
    } catch (e) {
      console.error('[meiqia] script load error', e);
      initPromise = null;
      return false;
    }

    // 3) 使用真实 _MEIQIA（切勿再覆盖）
    const MQ = mq();
    if (typeof MQ !== 'function') {
      console.error('[meiqia] SDK 未就绪');
      initPromise = null;
      return false;
    }

    return new Promise((resolve) => {
      let settled = false;
      const done = (ok) => {
        if (settled) return;
        settled = true;
        ready = !!ok;
        if (!ok) initPromise = null;
        resolve(!!ok);
      };

      try {
        MQ('allSet', () => {
          startPinCornerWatch();
          done(true);
        });
        MQ('init');
      } catch (e) {
        console.error('[meiqia] init failed', e);
        done(false);
        return;
      }

      // allSet 兜底
      setTimeout(() => {
        if (!settled) {
          console.warn('[meiqia] allSet 超时，尝试直接可用');
          startPinCornerWatch();
          done(true);
        }
      }, 8000);
    });
  })();

  return initPromise;
}

export function showMeiqiaPanel(opts) {
  const MQ = mq();
  if (typeof MQ !== 'function') {
    console.warn('[meiqia] 尚未初始化');
    return;
  }
  try {
    if (opts) {
      MQ('showPanel', opts);
    } else {
      MQ('showPanel');
    }
    // 打开面板后美洽可能重写按钮位置，再贴一次
    setTimeout(applyButtonCornerPin, 0);
    setTimeout(applyButtonCornerPin, 400);
  } catch (e) {
    console.error('[meiqia] showPanel failed', e);
  }
}

export function isMeiqiaReady() {
  return ready;
}
